import { useEffect, useState } from 'react'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import type { Order } from '@/api/orders/orders-types'

/**
 * `bulkScan` survives AHK browser navigation, keeping the skid sheet alive across
 * consecutive scans into the same skid.
 * - `mode: 'bundles'` — SKI/LHB sheet bulk-scanning bundles. AHK strips `?skid=` and
 *   adds `?bundle=<id>`; we read the skid identity from here as a fallback.
 * - `mode: 'lhbs'`   — MOS/OUT sheet bulk-scanning LHBs. AHK flips `?skid=<MOS>` to
 *   `?skid=<LHB>`; we treat the incoming label as an attach action and re-pin
 *   `?skid=<MOS>` so the sheet stays at the multi-order unit.
 *
 * `skidId` is the resolved numeric id used for mutation; `skidLabel` re-populates
 * `?skid=` after each navigation. `setAt` is a wall-clock timestamp used to expire
 * stale persisted sessions on rehydrate (see `onRehydrateStorage` below).
 */
export type SkidBulkScan = {
  skidId: number
  skidLabel: string
  mode: 'bundles' | 'lhbs'
  setAt: number
}

/**
 * `bundleScanActive` mirrors `bulkScan` for the inverse flow: a bundle-scan session
 * survives an AHK navigation that strips `?bundle=` and adds `?skid=` so the bundle
 * sheet stays at the item, and the scanned skid is consumed as the shipping unit
 * assignment instead of opening the skid sheet. `setAt` expires stale sessions on
 * rehydrate.
 */
export type BundleScanActive = { bundleId: number; setAt: number }

interface SkidPanelState {
  /** Order passed in by the opener — lets the sheet render the shipping sticker without refetching. */
  order: Order | null
  setOrder: (order: Order | null) => void
  /** Hide mutating actions (detach, reset, reopen, scan) — for archive/read-only contexts. */
  readOnly: boolean
  setReadOnly: (readOnly: boolean) => void
  bulkScan: SkidBulkScan | null
  setBulkScan: (bulkScan: Omit<SkidBulkScan, 'setAt'> | null) => void
  bundleScanActive: BundleScanActive | null
  setBundleScanActive: (bundleScanActive: Omit<BundleScanActive, 'setAt'> | null) => void
}

// TTL for persisted scan sessions. A full bundle→skid round-trip via the AHK helper
// completes in a couple seconds at most. Setters refresh `setAt` on every scan, so
// an actively-scanning session can't expire mid-flow. Keep the window short so a
// session abandoned a minute ago can't hijack the next operator's first scan;
// originally 5 minutes, dropped after a packaging session showed cross-order
// state lingering long after the operator moved on.
const PERSISTED_SCAN_TTL_MS = 60 * 1000

// Persist `bulkScan` and `bundleScanActive` in sessionStorage so they survive a
// full-page reload triggered by the AHK helper's Ctrl+L → paste → Enter flow
// (the address-bar navigation reloads the SPA, wiping in-memory zustand state).
// Scoped to sessionStorage so a closed tab clears the session and we don't carry
// a stale bulk-scan into the next visit. `order` and `readOnly` are render-time
// concerns and intentionally NOT persisted.
//
// The setters wrap the inputs so `setAt` is always Date.now() at the moment of
// writing. Callers pass the meaningful fields and don't have to remember the
// timestamp.
export const useSkidPanelStore = create<SkidPanelState>()(
  persist(
    (set) => ({
      order: null,
      setOrder: (order) => set({ order }),
      readOnly: false,
      setReadOnly: (readOnly) => set({ readOnly }),
      bulkScan: null,
      setBulkScan: (bulkScan) =>
        set({ bulkScan: bulkScan ? { ...bulkScan, setAt: Date.now() } : null }),
      bundleScanActive: null,
      setBundleScanActive: (bundleScanActive) =>
        set({
          bundleScanActive: bundleScanActive
            ? { ...bundleScanActive, setAt: Date.now() }
            : null
        })
    }),
    {
      name: 'skid-panel',
      version: 1,
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        bulkScan: state.bulkScan,
        bundleScanActive: state.bundleScanActive
      }),
      // Drop entries that pre-date the v1 schema (no `setAt`). They'd be
      // indefinitely "fresh" otherwise and could silently pin the sheet open on
      // an unrelated page load.
      migrate: () => ({ bulkScan: null, bundleScanActive: null }),
      // After rehydration, expire entries older than the TTL. Wide enough to cover
      // an in-flight AHK reload race and a brief operator pause, but kills truly
      // stale state from a prior task that would otherwise silently block the
      // skid sheet from opening.
      onRehydrateStorage: () => (state) => {
        if (!state) return
        const now = Date.now()
        if (state.bulkScan && now - state.bulkScan.setAt > PERSISTED_SCAN_TTL_MS) {
          state.bulkScan = null
        }
        if (
          state.bundleScanActive &&
          now - state.bundleScanActive.setAt > PERSISTED_SCAN_TTL_MS
        ) {
          state.bundleScanActive = null
        }
      }
    }
  )
)

/**
 * `persist` rehydrates from sessionStorage *asynchronously* (via a microtask) even
 * when the storage backend itself is sync. Between store creation and that microtask,
 * `bundleScanActive` / `bulkScan` read as their initial values (null), even if a
 * value lives in sessionStorage.
 *
 * That race opens a real bug: SkidDetailsSheet's `open` gate is `bundleScanActive == null`.
 * Right after an AHK reload from `?bundle=<id>` to `?skid=<label>`, the sheet would
 * briefly see `null` and pop open on top of the user's bundle flow, then close once
 * hydration finishes. Components that need the persisted scan state to be authoritative
 * must wait for hydration before deciding to open.
 *
 * Returns true once rehydration has run (whether a value was loaded or not).
 */
export function useSkidPanelHasHydrated(): boolean {
  const [hydrated, setHydrated] = useState(() =>
    useSkidPanelStore.persist.hasHydrated()
  )
  useEffect(() => {
    if (hydrated) return
    const unsub = useSkidPanelStore.persist.onFinishHydration(() => setHydrated(true))
    if (useSkidPanelStore.persist.hasHydrated()) setHydrated(true)
    return unsub
  }, [hydrated])
  return hydrated
}
